import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const validGrid = grid => Array.isArray(grid) && grid.length === 7
  && grid.every(row => Array.isArray(row) && row.length === 24
    && row.every(value => typeof value === 'number' && Number.isFinite(value) && value >= 0.5 && value <= 2.5));
const draftOf = grid => grid.map(row => row.map(String));

export default function ReferenceGridEditor({ onApplied }) {
  const [loaded, setLoaded] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const inFlight = useRef(false);
  const adopt = data => {
    if (!validGrid(data?.grid) || !/^[a-f0-9]{64}$/.test(data?.revision || '')) throw new Error('invalid_grid');
    setLoaded(data); setDraft(draftOf(data.grid)); setConfirming(false);
  };
  useEffect(() => {
    let cancelled = false;
    api.get('/admin/engine/eichung').then(({ data }) => {
      if (!cancelled) adopt(data);
    }).catch(() => { if (!cancelled) setError('Das Zeitgitter konnte nicht geladen werden.'); });
    return () => { cancelled = true; };
  }, []);
  const values = draft?.map(row => row.map(value => value.trim() ? Number(value.replace(',', '.')) : NaN));
  const valid = validGrid(values);
  const changed = loaded && values ? values.reduce((n, row, day) => n + row.filter((v, h) => v !== loaded.grid[day][h]).length, 0) : 0;
  const save = async () => {
    if (inFlight.current || !valid || !changed) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const { data } = await api.post('/admin/engine/eichung/apply', { grid: values, expected_revision: loaded.revision });
      adopt(data); onApplied?.(data.grid);
    } catch (e) {
      setError(e?.response?.status === 409
        ? 'Das Zeitgitter wurde inzwischen geändert. Dein Entwurf bleibt erhalten. Lade den aktuellen Stand bewusst neu.'
        : 'Anwenden fehlgeschlagen. Dein Entwurf bleibt erhalten.');
      setConfirming(false);
    } finally { inFlight.current = false; setBusy(false); }
  };
  const reload = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true);
    try { const { data } = await api.get('/admin/engine/eichung'); adopt(data); setError(''); }
    catch { setError('Der aktuelle Stand konnte nicht geladen werden.'); }
    finally { inFlight.current = false; setBusy(false); }
  };
  return <section aria-label="Marktreferenz-Zeitgitter">
    <h3>Eichung bearbeiten — Tag für Tag, Stunde für Stunde</h3>
    <p>Diese Werte ändern nur Marktvergleich und Analyse. Fahrpreise und Nachfrage-Wochenprofile bleiben unverändert. Zulässig sind 0,5 bis 2,5.</p>
    {error && <p role="alert">{error}</p>}
    {!draft ? <p>Zeitgitter wird geladen …</p> : <>
      <div style={{ overflowX: 'auto' }}><table><caption>Marktreferenz je Wochentag und Stunde</caption>
        <thead><tr><th>Tag</th>{Array.from({ length: 24 }, (_, h) => <th key={h}>{String(h).padStart(2, '0')}</th>)}</tr></thead>
        <tbody>{DAYS.map((day, d) => <tr key={day}><th scope="row">{day}</th>{draft[d].map((value, h) => <td key={h}>
          <input aria-label={`${day} ${h} Uhr`} type="text" inputMode="decimal" value={value} disabled={busy || confirming}
            style={{ width: 54 }} onChange={e => {
              const next = e.target.value;
              setDraft(prev => prev.map((row, i) => i === d ? row.map((v, j) => j === h ? next : v) : row));
            }} />
        </td>)}</tr>)}</tbody>
      </table></div>
      <p aria-live="polite">{changed} von 168 Zellen geändert.{!valid && ' Bitte alle Werte zwischen 0,5 und 2,5 eingeben.'}</p>
      <button type="button" disabled={busy || confirming} onClick={reload}>Aktuellen Stand neu laden und Entwurf verwerfen</button>{' '}
      <button type="button" disabled={busy || confirming || !validGrid(loaded.defaults)} onClick={() => setDraft(draftOf(loaded.defaults))}>Code-Standard als Entwurf übernehmen</button>{' '}
      <button type="button" disabled={busy || confirming || !valid || !changed} onClick={() => setConfirming(true)}>Änderungen prüfen und anwenden</button>
      {confirming && <div role="dialog" aria-label="Eichung bestätigen">
        <p>{changed} geänderte Zellen für Marktvergleich und Analyse übernehmen?</p>
        <button type="button" disabled={busy} onClick={() => setConfirming(false)}>Abbrechen</button>{' '}
        <button type="button" disabled={busy} onClick={save}>{busy ? 'Wird angewendet …' : 'Eichung verbindlich anwenden'}</button>
      </div>}
    </>}
  </section>;
}
